// @ts-nocheck
import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Box,
  ButtonBase,
  IconButton,
  InputAdornment,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';

import { ReactComponent as OpenInNewTabIcon } from '../../../assets/Icons/lucideIcons/square-arrow-out-up-right.svg';
import { ReactComponent as HistoryIcon } from '../../../assets/Icons/lucideIcons/history.svg';
import { ReactComponent as VisibilityIcon } from '../../../assets/Icons/lucideIcons/eye.svg';

// Import all field components from the index file
import {
  TextFieldComponent,
  IntegerFieldComponent,
  DecimalFieldComponent,
  DateFieldComponent,
  DropdownFieldComponent,
  MultiSelectFieldComponent,
  UserSelectFieldComponent,
  GroupSelectFieldComponent,
  AssociationFieldComponent,
  SegmentsAttribute,
  AttributeListFieldComponent,
  ParameterTableFieldComponent,
  EmailTemplateListFieldComponent,
  TemplateListFieldComponent,
  FieldChangeActionsComponent,
  InventoryViewAttribute,
  AssociationFieldTableViewComponent,
  CanvasFieldRenderer,
} from './FieldComponents';
import MultiEditModal from './Modals/MultiEditModal';
import GroupMultiSelectFieldComponent from './FieldComponents/GroupMultiSelectFieldComponent';
import UserMultiSelectFieldComponent from './FieldComponents/UserMultiSelectFieldComponent';
import {
  EMPTY_ARRAY,
  EMPTY_OBJECT,
  getFilteredEntityTypes,
  NOOP,
} from '../../../const/CommonConst';
import { LazyHtmlAttributePreviewLink } from './HtmlPreviewDialogLink';
import DocumentAttributeLink from './ViewAttributes/DocumentAttributeLink';
import {
  checkAttributeIsRequired,
  formatAttributeValue,
  isValueEmpty,
} from '../../../utils/Utils';
import { ReactComponent as CalculationIcon } from '../../../assets/images/CalculationIcon.svg';
import FieldChangeOtherDetails from './FieldComponents/FieldChangeOtherDetails';
import AISuggestionIndicator from '../AIAutofill/AISuggestionIndicator';
import { useUploadDocumentContext } from '../../../context/DocumentUpload/UploadDocumentContext';
import useAttributeDocumentPreview from '../../../hooks/useAttributeDocumentPreview';
import DocumentPreviewModal from '../../../features/document/components/DocumentPreviewModal';
import {
  buildDraftChipTooltip,
  draftValuesEqual,
  getFormikValueByAttributeId,
  getValueByAttributeId,
  useDraftChipMeta,
  useShowDraftChip,
  useDraftAttributeValue,
} from '../../../hooks/useDraftAutoSave.jsx';
import {
  DraftAttributeChip,
  DraftFieldFooter,
  DraftLastEditedText,
  DraftNewVariantFooter,
  DESCRIPTIVE_DRAFT_INPUT_BORDER_SX,
  DRAFT_EXTRA_TEXT_SX,
  NEW_VARIANT_ATTRIBUTE_LABEL_SX,
} from './FieldComponents/DraftFieldExtras';

import {
  useGroupsQuery,
  useUsersQuery,
} from '../../../features/administrativeOptions/userManagement/api/UserManagementQueries';

const MODAL_DATA_TYPES = [
  'Table',
  'AdvancedTable',
  'Chart',
  'Checklist',
  'Document',
  'Paragraph',
];

// Input control types that should open in modal instead of inline editing
const MODAL_INPUT_TYPES = ['TEXTFIELD_EXTENDED', 'Canvas'];

const attributeRequiresTeamUsers = ['assignedUser'];

const tableValueCount = (value) => {
  if (value == null) return 0;
  if (Array.isArray(value?.data)) return value.data.length;
  if (Array.isArray(value)) return value.length;
  return Object.keys(value?.Cells || {}).length;
};

/**
 * Resolve a short display string for the current value so the input shows
 * a meaningful summary instead of "[object Object]".
 */
const getDisplayValue = (dataType, inputType, value, docUploadStatus) => {
  if (value == null) return '';

  if (dataType === 'Chart' && value) {
    return 'Configured Chart';
  }

  if (typeof value === 'string') return value;

  if (dataType === 'Checklist' && value?.checkedEntity) {
    const items = Object.keys(value.checkedEntity);
    const done = Object.values(value.checkedEntity).filter(Boolean).length;
    if (items.length === 0) return '';
    return `${done} of ${value.totalCount ?? items.length} checked`;
  }
  if (dataType === 'Table' || dataType === 'AdvancedTable') {
    const count = tableValueCount(value);
    if (count === 0) return '';
    const unit = dataType === 'AdvancedTable' ? 'cell' : 'row';
    return `Configured ${count} ${unit}${count > 1 ? 's' : ''}`;
  }
  if (dataType === 'Document') {
    if (docUploadStatus) {
      if (docUploadStatus.hasError) return 'Upload failed';
      if (docUploadStatus.isProcessing) return 'Processing…';
      if (docUploadStatus.isQueued) return 'Upload in progress…';
      if (docUploadStatus.isUploading)
        return `Uploading… ${Math.round(docUploadStatus.progress)}%`;
    }
    if (Array.isArray(value)) {
      if (value?.length > 1) {
        return `${value.length} file${value.length > 1 ? 's' : ''}`;
      } else if (value?.length === 1) {
        return value[0].document_name;
      }
      return '';
    }
    if (value && typeof value === 'object' && value.document_name) {
      return value.document_name;
    }
  }
  if (dataType === 'Paragraph' || inputType === 'TEXTFIELD_EXTENDED') {
    const title = value?.title?.trim?.() ?? '';
    const description = value?.description?.trim?.() ?? '';
    return title || description || '';
  }
  if (typeof value === 'object' && Object.keys(value).length > 0)
    return 'Configured';
  return '';
};

/**
 * A component for multi-edit mode that renders inline fields for simple data types
 * and modal triggers for complex data types
 * @param {Object} props - Component props
 * @param {Object} props.attribute - The attribute object with metadata
 * @param {any} props.value - Current value of the attribute
 * @param {Function} props.onChange - Function to handle value changes
 * @param {Array} props.associationList - List of associations (for Association type)
 * @param {Object} props.entityData - Entity data for document fields
 * @returns {React.ReactElement} The appropriate field component or modal trigger
 */
const MultiEditAttributeField = ({
  attribute,
  value,
  onChange,
  entityData = EMPTY_OBJECT,
  showLabel = true,
  entityDetailsFormikForm,
  teamUsersList = EMPTY_ARRAY,
  handleOpenDerivedModal,
  handleAutoSave,
  hideHeader,
  isUserAuthorizedToOverrideAttribute = false,
  // Constraint-related props
  lookupSourceAttributes = EMPTY_ARRAY,
  autoCompleteSourceAttributes = EMPTY_ARRAY,
  overrideDropdownOptions = EMPTY_OBJECT,
  isLoadingConstraints = false,
  dateCalendarConstraints = EMPTY_OBJECT,
  dateCalendarSourceAttributes = EMPTY_ARRAY,
  editableSourceAttributes = EMPTY_ARRAY,
  mandatorySourceAttributes = EMPTY_ARRAY,
  groupDetails = EMPTY_OBJECT,
  // Explicit props for SegmentsAttribute and others
  isUserAuthorizedForUpdateAttribute,
  breadcrumbDetails,
  deleteNote,
  editNote,
  handleOpenNoteModal,
  handleSetAddNoteModalHeading,
  handleSetSelectedEntityToAddInEditor,
  getAttComments,
  allAttributeChats,
  getOverrideAttributeValue,
  isMultipleEditMode,
  createNewSegmentRef,
  copySegmentRef,
  segmentButtonState,
  entitySelectedGroupId,
  prevNodeGroupSelected,
  attributeValues,
  savedAttributeValues = EMPTY_OBJECT,
  // AI Autofill props
  aiSuggestion,
  onAcceptAISuggestion,
  onRejectAISuggestion,
  onUndoAISuggestion,
  configColumn = false,
  modelTemplateDetails = EMPTY_OBJECT,
  isNewVariant = false,
  showDraftFooter = true,
  descriptiveDraftFooter = false,
  endAdornmentExtra = null,
  onHistoryClick = NOOP,
}) => {
  const theme = useTheme();
  const showHistoryIcon = useMemo(
    () => Boolean(attribute?.show_history),
    [attribute?.show_history]
  );
  const handleHistoryClick = useCallback(
    (e) => {
      e?.stopPropagation();
      onHistoryClick?.(attribute);
    },
    [onHistoryClick, attribute]
  );
  const { editorName: editorDisplayName = '', updatedAt: draftUpdatedAt } =
    useDraftChipMeta(attribute?.id);
  const draftChipTooltip = useMemo(
    () => buildDraftChipTooltip(editorDisplayName, draftUpdatedAt),
    [editorDisplayName, draftUpdatedAt]
  );
  const labelColor = theme.palette.attributesForm?.label;
  const attributeLabelSx = useMemo(
    () => ({
      fontWeight: 500,
      fontSize: '12px',
      lineHeight: '18px',
      color: labelColor,
      maxWidth: 360,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    }),
    [labelColor]
  );
  const editableAttributeLabelSx = useMemo(
    () => ({ ...attributeLabelSx, color: `${labelColor} !important` }),
    [attributeLabelSx, labelColor]
  );

  const { pluginConfig } = useSelector((state) => state?.tenant);
  const isHDFCUAMEnabled = useMemo(
    () => !!pluginConfig?.HDFCUAM,
    [pluginConfig]
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [resolvedAssociationValue, setResolvedAssociationValue] =
    useState(null);
  const uploadContext = useUploadDocumentContext();
  const activeBatches = uploadContext?.activeBatches || [];
  const uploadProgress = uploadContext?.uploadProgress || {};

  const uploadErrors = uploadContext?.uploadErrors || {};

  const { data: groupList = EMPTY_ARRAY } = useGroupsQuery();

  /**
   * Derive detailed upload status for this document attribute:
   * - progress: number (0-100)
   * - hasError: boolean
   * - isUploading: boolean (file is in progress, not yet 100%)
   * - isProcessing: boolean (progress=100, no error, waiting for SSE)
   * - fileName: string
   */
  const docUploadStatus = useMemo(() => {
    if (attribute?.data_type !== 'Document') return null;
    for (const batch of activeBatches) {
      const isEntityMatch =
        String(batch.entityId) === String(entityData?.entity_id) ||
        String(batch.uniqueId) === String(entityData?.unique_id);

      if (!isEntityMatch) continue;

      for (const file of batch.data || []) {
        if (
          file.attribute_id === attribute?.id &&
          file.document_from_attribute
        ) {
          const progress = uploadProgress[file.key] || 0;
          const hasError = !!uploadErrors[file.key];
          const isQueued = uploadProgress[file.key] === undefined && !hasError;
          return {
            progress,
            hasError,
            isUploading: !isQueued && progress < 100 && !hasError,
            isProcessing: progress === 100 && !hasError,
            isQueued,
            fileName: file.document_name || file.file?.name || 'Document',
          };
        }
      }
    }
    return null;
  }, [attribute, activeBatches, uploadProgress, uploadErrors, entityData]);

  const isRequired = useMemo(() => {
    return checkAttributeIsRequired(attribute);
  }, [attribute]);

  const isLoadingFieldExceptSourceAttributes = useMemo(() => {
    // If the current attribute is a lookup source attribute, don't show loading
    if (
      lookupSourceAttributes?.includes(Number.parseInt(attribute?.id)) ||
      autoCompleteSourceAttributes?.includes(Number.parseInt(attribute?.id)) ||
      dateCalendarSourceAttributes?.includes(Number.parseInt(attribute?.id)) ||
      editableSourceAttributes?.includes(Number.parseInt(attribute?.id)) ||
      mandatorySourceAttributes?.includes(Number.parseInt(attribute?.id))
    ) {
      return false;
    }
    // For other attributes, return the loading state
    return isLoadingConstraints;
  }, [
    lookupSourceAttributes,
    autoCompleteSourceAttributes,
    dateCalendarSourceAttributes,
    editableSourceAttributes,
    mandatorySourceAttributes,
    isLoadingConstraints,
    attribute?.id,
  ]);
  const entityTypes = useMemo(() => {
    return getFilteredEntityTypes() || [];
  }, []);

  const renderEntityLabel = useCallback(
    (entity_type) => {
      const matchedEntityType = entityTypes.find(
        (type) => type?.value === entity_type
      );

      return matchedEntityType?.label;
    },
    [entityTypes]
  );
  const buildAssociationRedirectUrl = (associationValue) => {
    if (!associationValue?.entity_type || !associationValue?.unique_id)
      return null;

    const currentUniqueId = associationValue?.unique_id;

    const dynamicEntityPath = renderEntityLabel(associationValue?.entity_type);

    return `/${dynamicEntityPath}/${encodeURIComponent(currentUniqueId)}`;
  };

  const { data: usersList = EMPTY_ARRAY } = useUsersQuery();

  const finalUsersList = useMemo(() => {
    if (
      attribute?.data_type === 'User' &&
      attributeRequiresTeamUsers.includes(attribute?.name)
    ) {
      return teamUsersList;
    }
    return usersList;
  }, [attribute?.data_type, attribute?.name, usersList, teamUsersList]);

  const showDraftChip = useShowDraftChip(attribute) && !configColumn;
  const entityDraftValue = useDraftAttributeValue(attribute?.id);
  const draftAttributeValue = configColumn ? undefined : entityDraftValue;
  const useDraftInputHighlight = showDraftChip && !descriptiveDraftFooter;
  const formikVal =
    entityDetailsFormikForm?.values?.[attribute?.id] ??
    entityDetailsFormikForm?.values?.[Number(attribute?.id)];
  const formikResolved = getValueByAttributeId(
    entityDetailsFormikForm?.values,
    attribute?.id
  );
  const activeValue = (() => {
    const type = attribute?.data_type;
    if (type !== 'Table' && type !== 'AdvancedTable') {
      if (formikResolved !== undefined) return formikResolved;
      return (
        (showDraftChip && draftAttributeValue !== undefined
          ? draftAttributeValue
          : null) ?? value
      );
    }
    if (showDraftChip) {
      if (draftAttributeValue !== undefined) return draftAttributeValue;
      if (tableValueCount(formikVal) > 0) return formikVal;
      return getValueByAttributeId(attributeValues, attribute?.id) ?? formikVal;
    }
    const saved = getValueByAttributeId(attributeValues, attribute?.id);
    return tableValueCount(formikVal) > 0 ? formikVal : (saved ?? formikVal);
  })();

  const existingDocument = useMemo(() => {
    if (
      attribute?.data_type !== 'Document' ||
      attribute?.input_control_type === 'MULTI_DOCUMENT'
    ) {
      return null;
    }
    if (Array.isArray(activeValue)) return activeValue[0] || null;
    if (activeValue && typeof activeValue === 'object') return activeValue;
    return null;
  }, [attribute?.data_type, attribute?.input_control_type, activeValue]);
  const { previewDocument, previewModalProps } = useAttributeDocumentPreview({
    entityId: entityData?.entity_id,
    entityType: entityData?.entity_type,
  });
  const handlePreviewExistingDocument = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (!existingDocument?.document_id) return;
      previewDocument(existingDocument.document_id);
    },
    [existingDocument, previewDocument]
  );

  const attributeOverrideOptions = useMemo(() => {
    const attributeId = attribute?.id;
    return overrideDropdownOptions[attributeId] || null;
  }, [overrideDropdownOptions, attribute?.id]);

  const savedOriginalValue = useMemo(() => {
    const fromSaved = getValueByAttributeId(
      savedAttributeValues,
      attribute?.id
    );
    const fromInitial = getValueByAttributeId(
      entityDetailsFormikForm?.initialValues,
      attribute?.id
    );
    if (!showDraftChip) {
      return fromSaved;
    }
    const draftVal =
      draftAttributeValue ??
      getFormikValueByAttributeId(entityDetailsFormikForm, attribute?.id);
    for (const candidate of [fromSaved, fromInitial]) {
      if (
        candidate !== undefined &&
        !draftValuesEqual(draftVal, candidate, attribute, finalUsersList)
      ) {
        return candidate;
      }
    }
    return draftVal !== undefined ? null : undefined;
  }, [
    attribute,
    showDraftChip,
    savedAttributeValues,
    entityDetailsFormikForm,
    draftAttributeValue,
    finalUsersList,
  ]);

  const formattedSavedOriginal = useMemo(() => {
    if (savedOriginalValue === undefined) {
      return null;
    }

    let valueForDisplay = savedOriginalValue;
    if (
      attribute?.data_type === 'Character' &&
      attribute?.input_control_type === 'SINGLE_SELECT'
    ) {
      if (typeof savedOriginalValue === 'object' && savedOriginalValue?.label) {
        valueForDisplay = savedOriginalValue.label;
      } else {
        const options =
          attributeOverrideOptions ?? attribute?.drop_down_options ?? [];
        const match = (Array.isArray(options) ? options : []).find((option) => {
          const optionKey =
            typeof option === 'object'
              ? (option?.value ?? option?.entity_id)
              : option;
          return String(optionKey) === String(savedOriginalValue);
        });
        if (match != null) {
          valueForDisplay =
            typeof match === 'object' ? match.label || match.value : match;
        }
      }
    }

    // Draft comparisons show just the document name as a link, not the full
    // icon + name + preview button used in the live attribute view.
    if (attribute?.data_type === 'Document' && valueForDisplay) {
      return (
        <DocumentAttributeLink
          documentDetails={valueForDisplay}
          entityId={entityData?.entity_id}
          entityType={entityData?.entity_type}
        />
      );
    }

    return formatAttributeValue(
      valueForDisplay,
      attribute,
      false,
      entityData?.entity_id,
      entityDetailsFormikForm,
      finalUsersList,
      groupList,
      entityData?.entity_type,
      undefined,
      false,
      false,
      entityData
    );
  }, [
    savedOriginalValue,
    attribute,
    attributeOverrideOptions,
    entityData?.entity_id,
    entityData?.entity_type,
    entityDetailsFormikForm,
    finalUsersList,
    groupList,
  ]);

  const dataType = attribute?.data_type;
  const inputType = attribute?.input_control_type;
  const label = attribute?.label;
  const associationRedirectUrl = useMemo(() => {
    if (dataType !== 'Association') return null;
    return buildAssociationRedirectUrl(resolvedAssociationValue || activeValue);
  }, [activeValue, dataType, resolvedAssociationValue]);

  const showAssociationRedirectIcon = Boolean(associationRedirectUrl);
  const associationRedirectTooltip = resolvedAssociationValue?.entity_alias
    ? `${resolvedAssociationValue.entity_alias} (${resolvedAssociationValue.unique_id})`
    : 'Open associated entity';
  const handleResolvedAssociationChange = useCallback((association) => {
    setResolvedAssociationValue((prevAssociation) => {
      if (
        prevAssociation?.entity_id === association?.entity_id &&
        prevAssociation?.unique_id === association?.unique_id
      ) {
        return prevAssociation;
      }
      return association;
    });
  }, []);
  const handleAssociationRedirectClick = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (associationRedirectUrl) {
        window.open(associationRedirectUrl, '_blank');
      }
    },
    [associationRedirectUrl]
  );
  const isDerived = hideHeader
    ? attribute?.is_derived && attribute?.permission === 'manage'
    : attribute?.is_derived;

  const shouldOpenInModal = useMemo(() => {
    return (
      MODAL_DATA_TYPES.includes(dataType) ||
      MODAL_INPUT_TYPES.includes(inputType)
    );
  }, [dataType, inputType]);

  const buildFieldLabel = useCallback(
    (fieldLabel) => (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: showDraftChip || isNewVariant ? '100%' : undefined,
          maxWidth: showDraftChip || isNewVariant ? '100%' : 280,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            minWidth: 0,
            flex: showDraftChip || isNewVariant ? 1 : undefined,
          }}
        >
          <Typography
            variant="attributeLabel"
            sx={
              isNewVariant
                ? {
                    ...NEW_VARIANT_ATTRIBUTE_LABEL_SX,
                    maxWidth: '100%',
                    whiteSpace: 'normal',
                    overflowWrap: 'anywhere',
                  }
                : editableAttributeLabelSx
            }
            title={attribute?.label}
          >
            {fieldLabel}
            {/* New variant labels wrap, so keep the asterisk and draft chip
                inline to sit right after the last word instead of at the
                box edge. */}
            {isRequired && isNewVariant && (
              <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>
                *
              </Box>
            )}
            {showDraftChip && isNewVariant && (
              <Box
                component="span"
                sx={{ display: 'inline-block', verticalAlign: 'middle' }}
              >
                <DraftAttributeChip
                  isNewVariant={isNewVariant}
                  draftChipTooltip={draftChipTooltip}
                />
              </Box>
            )}
          </Typography>
          {isRequired && !isNewVariant && (
            <Box
              component="span"
              sx={{
                color: 'error.main',
                ml: 0.5,
                flexShrink: 0,
                lineHeight: 1,
              }}
            >
              *
            </Box>
          )}
          {showAssociationRedirectIcon && (
            <Tooltip title={associationRedirectTooltip}>
              <IconButton
                aria-label={`Open ${attribute?.label || 'associated entity'}`}
                size="small"
                sx={{ color: labelColor }}
                onClick={handleAssociationRedirectClick}
              >
                <OpenInNewTabIcon width={12} height={12} />
              </IconButton>
            </Tooltip>
          )}
          {showDraftChip && !isNewVariant && (
            <DraftAttributeChip
              isNewVariant={isNewVariant}
              draftChipTooltip={draftChipTooltip}
            />
          )}
          {showHistoryIcon && (
            <Tooltip title="History">
              <ButtonBase
                onClick={handleHistoryClick}
                aria-label={`View history for ${attribute?.label || attribute?.name}`}
                sx={{ color: 'primary.main', flexShrink: 0, ml: 0.5 }}
              >
                <HistoryIcon width={16} height={16} />
              </ButtonBase>
            </Tooltip>
          )}
        </Box>
        {showDraftChip && isNewVariant && formattedSavedOriginal != null && (
          <Typography
            variant="caption"
            component="div"
            sx={{
              ...DRAFT_EXTRA_TEXT_SX,
              ml: 'auto',
              flexShrink: 0,
              maxWidth: '40%',
              textAlign: 'left',
              fontWeight: 400,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              '& .MuiTypography-root': {
                ...DRAFT_EXTRA_TEXT_SX,
                fontWeight: 400,
              },
            }}
          >
            <Box
              component="span"
              sx={{ flexShrink: 0, whiteSpace: 'nowrap', fontWeight: 600 }}
            >
              Original Value:
            </Box>
            <Tooltip
              arrow
              title={
                typeof formattedSavedOriginal === 'string' ? (
                  <Typography
                    variant="caption"
                    component="div"
                    sx={{ whiteSpace: 'pre-line' }}
                  >
                    {formattedSavedOriginal}
                  </Typography>
                ) : (
                  ''
                )
              }
            >
              <Box
                component="span"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  minWidth: 0,
                }}
              >
                {formattedSavedOriginal}
              </Box>
            </Tooltip>
          </Typography>
        )}
        {showDraftChip && !isNewVariant && (
          <DraftLastEditedText editorName={editorDisplayName} />
        )}
      </Box>
    ),
    [
      showDraftChip,
      showHistoryIcon,
      handleHistoryClick,
      isNewVariant,
      editableAttributeLabelSx,
      attribute?.label,
      attribute?.name,
      attribute?.id,
      isRequired,
      editorDisplayName,
      draftChipTooltip,
      formattedSavedOriginal,
      showAssociationRedirectIcon,
      associationRedirectTooltip,
      handleAssociationRedirectClick,
      labelColor,
    ]
  );

  const handleDraftRevert = useCallback(() => {
    const attrId = attribute?.id;
    if (attrId == null) {
      return;
    }
    // Reset accepted AI suggestion status → pending without touching the value,
    // so the revert owns the value and the suggestion remains visible/re-actionable.
    if (onUndoAISuggestion) {
      onUndoAISuggestion(attrId, { skipValueChange: true });
    }
    const original =
      savedOriginalValue !== undefined
        ? savedOriginalValue
        : (savedAttributeValues?.[attrId] ??
          savedAttributeValues?.[Number(attrId)] ??
          null);
    onChange(original);
    if (isMultipleEditMode && handleAutoSave) {
      handleAutoSave(attrId, original, { persist: true, revert: true });
    }
  }, [
    savedOriginalValue,
    savedAttributeValues,
    attribute?.id,
    onChange,
    isMultipleEditMode,
    handleAutoSave,
    onUndoAISuggestion,
  ]);

  const wrapWithDraftExtras = useCallback(
    (content) => {
      // Centers any trailing adornment (e.g. an info icon) against just the
      // input row, regardless of whether a label or draft footer is also
      // rendered above/below it.
      const fieldRow = endAdornmentExtra ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
            }}
          >
            {content}
          </Box>
          {endAdornmentExtra}
        </Box>
      ) : (
        content
      );
      const useExternalLabel =
        (isNewVariant || dataType === 'Association' || showHistoryIcon) &&
        showLabel;
      if (!showDraftChip && !useExternalLabel) {
        return fieldRow;
      }
      const isLongLabel = !isNewVariant && attribute?.label?.length > 90;
      const fieldLabel = isLongLabel
        ? attribute?.label?.slice(0, 90) + '...'
        : attribute?.label;
      return (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',

            gap:
              dataType === 'Association' && !showDraftChip && !isNewVariant
                ? 0
                : 0.5,

            ...(useExternalLabel && !showDraftChip
              ? { '& .MuiTextField-root': { marginTop: 0 } }
              : undefined),

            ...(showDraftChip && descriptiveDraftFooter
              ? DESCRIPTIVE_DRAFT_INPUT_BORDER_SX
              : undefined),
          }}
        >
          {showLabel && buildFieldLabel(fieldLabel)}
          {fieldRow}
          {showDraftChip &&
            showDraftFooter &&
            (isNewVariant ? (
              <DraftNewVariantFooter
                editorName={editorDisplayName}
                updatedAt={draftUpdatedAt}
                onRevert={handleDraftRevert}
                sx={{ mt: 0 }}
              />
            ) : (
              <DraftFieldFooter
                descriptive={descriptiveDraftFooter}
                attribute={attribute}
                originalValue={savedOriginalValue}
                currentDraftValue={activeValue}
                onRevert={handleDraftRevert}
                entityDetailsFormikForm={entityDetailsFormikForm}
                usersList={finalUsersList}
                groupList={groupList}
                entityId={entityData?.entity_id}
                entityType={entityData?.entity_type}
              />
            ))}
        </Box>
      );
    },
    [
      showDraftChip,
      showHistoryIcon,
      showLabel,
      showDraftFooter,
      isNewVariant,
      dataType,
      endAdornmentExtra,
      descriptiveDraftFooter,
      attribute,
      buildFieldLabel,
      savedOriginalValue,
      activeValue,
      handleDraftRevert,
      entityDetailsFormikForm,
      finalUsersList,
      groupList,
      entityData?.entity_id,
      entityData?.entity_type,
      editorDisplayName,
      draftUpdatedAt,
    ]
  );

  const isDerivedExtended = useMemo(() => {
    return isDerived && inputType === 'TEXTFIELD_EXTENDED';
  }, [isDerived, inputType]);

  // Handle opening modal
  const handleOpenModal = useCallback(() => {
    setIsModalOpen(true);
  }, [setIsModalOpen]);

  // Handle closing modal
  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, [setIsModalOpen]);

  const persistDraftOnBlur = useCallback(() => {
    if (!isMultipleEditMode || !handleAutoSave || attribute?.id == null) {
      return;
    }
    const attrId = attribute.id;
    queueMicrotask(() => {
      handleAutoSave(attrId, undefined, { persist: true });
    });
  }, [isMultipleEditMode, handleAutoSave, attribute?.id]);

  // Handle saving from modal
  const handleModalSave = useCallback(
    (newValue) => {
      onChange(newValue);
      // Draft is persisted exclusively by MultiEditModal's Save button via
      // persistDraftOnSave; calling persistDraftOnBlur here would fire the
      // draft API on every intermediate change (upload, remove, edit).
    },
    [onChange]
  );

  const handleModalTriggerBlur = useCallback(() => {
    if (!isModalOpen) {
      persistDraftOnBlur();
    }
  }, [isModalOpen, persistDraftOnBlur]);

  const hasError = useMemo(() => {
    const fieldId = attribute?.id;
    if (!fieldId || !entityDetailsFormikForm) return false;

    const { errors, touched } = entityDetailsFormikForm;

    return Boolean(errors?.[fieldId] && touched?.[fieldId]);
  }, [attribute?.id, entityDetailsFormikForm]);

  // Get modal button text based on data type and input type
  const getModalButtonText = useCallback(() => {
    const currentLabel = attribute?.label || attribute?.name;
    const hasValue = !isValueEmpty(value);
    if (inputType === 'TEXTFIELD_EXTENDED') {
      if (isDerivedExtended) return `View ${currentLabel}`;
      return hasValue ? `Edit - ${value}` : `Add ${currentLabel}`;
    }

    if (dataType === 'Table') {
      return isValueEmpty(activeValue) ? 'Upload Table' : 'Edit Table';
    }
    if (dataType === 'AdvancedTable') {
      return 'Edit Advanced Table';
    }
    if (dataType === 'Chart') {
      return isValueEmpty(activeValue) ? 'Upload Chart' : 'Edit Chart';
    }
    if (dataType === 'Checklist') {
      return 'Edit Checklist';
    }
    if (dataType === 'Document') {
      if (docUploadStatus) {
        if (docUploadStatus.hasError) {
          return 'Upload failed';
        }
        if (docUploadStatus.isProcessing) {
          return 'Processing…';
        }
        if (docUploadStatus.isQueued) {
          return 'Upload in progress…';
        }
        if (docUploadStatus.isUploading) {
          return `Uploading… ${Math.round(docUploadStatus.progress)}%`;
        }
      }
      if (attribute?.input_control_type === 'MULTI_DOCUMENT') {
        const count = Array.isArray(activeValue)
          ? activeValue.filter((d) => Boolean(d?.document_name)).length
          : 0;
        return count > 0
          ? `${count} Document${count > 1 ? 's' : ''}`
          : 'Upload Documents';
      }
      if (!isValueEmpty(activeValue) && activeValue?.[0]?.document_name) {
        return activeValue[0].document_name;
      }
      return isValueEmpty(activeValue) ? 'Upload Document' : 'Edit Document';
    }
    if (dataType === 'Paragraph') {
      return 'Edit Paragraph';
    }

    return `Edit ${attribute?.label || attribute?.name}`;
  }, [
    dataType,
    inputType,
    attribute?.label,
    attribute?.name,
    activeValue,
    isDerivedExtended,
    docUploadStatus,
  ]);
  // Helper function to render label with truncation and required indicator
  const createRenderedLabel = useCallback(() => {
    if (!attribute?.label) return null;

    const isLongLabel = !isNewVariant && attribute.label.length > 90;
    const fieldLabel = isLongLabel
      ? attribute.label.slice(0, 90) + '...'
      : attribute.label;

    return (
      <Box
        display="flex"
        alignItems="center"
        maxWidth={isNewVariant ? '100%' : 280}
        // Legacy fields render this node inside MUI's floating InputLabel.
        // Move only that label up to match the new variant's external label
        // spacing. Keep the new variant completely unaffected.
        sx={!isNewVariant ? { position: 'relative', top: '-4px' } : undefined}
      >
        <Typography
          variant="attributeLabel"
          sx={{
            ...(isNewVariant
              ? {
                  ...NEW_VARIANT_ATTRIBUTE_LABEL_SX,
                  whiteSpace: 'normal',
                  overflowWrap: 'anywhere',
                }
              : {
                  ...editableAttributeLabelSx,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }),
          }}
          title={attribute.label}
        >
          {fieldLabel}
        </Typography>
        {isRequired && (
          <Box
            component="span"
            sx={{ color: 'error.main', ml: 0.5, flexShrink: 0, lineHeight: 1 }}
          >
            *
          </Box>
        )}
      </Box>
    );
  }, [attribute?.label, isRequired, isNewVariant, editableAttributeLabelSx]);

  const handleClickAttribute = useCallback(
    (e) => {
      e.stopPropagation();
      handleOpenDerivedModal(attribute);
    },
    [handleOpenDerivedModal, attribute]
  );
  // Render modal trigger for complex data types
  const renderModalTrigger = useCallback(() => {
    const disabled = attribute?.is_editable === false;
    const displayValue = getDisplayValue(
      dataType,
      inputType,
      activeValue,
      docUploadStatus
    );
    const ph = 'Click to configure...';
    let modalLinkColor = 'primary.main';
    if (hasError) modalLinkColor = 'error.main';
    else if (showDraftChip) modalLinkColor = theme.palette.draft.accent;

    const isDoc = dataType === 'Document';
    const canOpenModal = !disabled || isDoc;

    if (isNewVariant) {
      const customEndAdornment = (
        <InputAdornment position="end">
          <OpenInNewTabIcon
            width={18}
            height={18}
            style={{
              cursor: canOpenModal ? 'pointer' : 'not-allowed',
              color: hasError
                ? theme.palette.error.main
                : theme.palette.text.secondary,
            }}
            onClick={canOpenModal ? handleOpenModal : undefined}
          />
        </InputAdornment>
      );

      return (
        <>
          <TextFieldComponent
            label=""
            titleLabel={attribute?.label}
            value={displayValue}
            onChange={NOOP}
            attribute={attribute}
            hideHeader={hideHeader}
            isLoadingConstraints={false}
            endAdornment={customEndAdornment}
            onClick={canOpenModal ? handleOpenModal : undefined}
            customPlaceholder={ph}
            showLabel={false}
            hasDraftChip={useDraftInputHighlight}
            sx={{
              cursor: canOpenModal ? 'pointer' : 'not-allowed',
              '& .MuiInputBase-input': {
                cursor: canOpenModal ? 'pointer' : 'not-allowed',
              },
            }}
          />

          {isModalOpen && (
            <MultiEditModal
              open={isModalOpen}
              handleClose={handleCloseModal}
              attribute={attribute}
              value={activeValue}
              handleSave={handleModalSave}
              groupList={groupList}
              entityData={entityData}
              entityDetailsFormikForm={entityDetailsFormikForm}
              handleAutoSave={handleAutoSave}
              isDerivedExtended={isDerivedExtended}
              usersList={finalUsersList}
              teamUsersList={teamUsersList}
              mode="edit"
              configColumn={configColumn}
              modelTemplateDetails={modelTemplateDetails}
              isUserAuthorizedToOverrideAttribute={
                isUserAuthorizedToOverrideAttribute
              }
            />
          )}
        </>
      );
    }

    const isLongLabel = !isNewVariant && attribute?.label?.length > 90;
    const fieldLabel = isLongLabel
      ? `${attribute?.label?.slice(0, 90)}...`
      : attribute?.label;

    const useExternalLabel =
      (isNewVariant || dataType === 'Association' || showHistoryIcon) &&
      showLabel;

    return (
      <>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
          }}
        >
          {showLabel &&
            !showDraftChip &&
            !useExternalLabel &&
            buildFieldLabel(fieldLabel)}
          <Box
            tabIndex={0}
            onBlur={handleModalTriggerBlur}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              height: '36px',
              pr: 1.9,
              outline: 'none',
            }}
          >
            {(() => {
              const modalTriggerLabel = (
                <Typography
                  variant="body2"
                  component="span"
                  onClick={handleOpenModal}
                  sx={{
                    color: modalLinkColor,
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {getModalButtonText()}
                </Typography>
              );

              if (
                dataType !== 'Document' ||
                !existingDocument ||
                inputType === 'MULTI_DOCUMENT'
              ) {
                return modalTriggerLabel;
              }

              // Grouped together (instead of as separate space-between
              // siblings) so the preview icon sits right next to the link
              // instead of being pushed to the far edge of a wide row.
              return (
                <Box display="flex" alignItems="center" gap={0.5} minWidth={0}>
                  {modalTriggerLabel}
                  <Tooltip title="Preview document">
                    <IconButton
                      size="small"
                      sx={{ flexShrink: 0 }}
                      onClick={handlePreviewExistingDocument}
                      aria-label={`Preview ${attribute?.label || 'document'}`}
                    >
                      <VisibilityIcon
                        width={18}
                        height={18}
                        style={{ color: theme.palette.primary.main }}
                      />
                    </IconButton>
                  </Tooltip>
                </Box>
              );
            })()}
            {isDerivedExtended && (
              <Tooltip
                title={
                  !attribute?.show_calculation_popUp
                    ? 'Enable the calculation popup to calculate this attribute.'
                    : `Calculate ${attribute?.label}`
                }
              >
                <CalculationIcon
                  sx={{
                    cursor: 'pointer',
                    flexShrink: 0,
                  }}
                  onClick={handleClickAttribute}
                />
              </Tooltip>
            )}
          </Box>
        </Box>
        {isModalOpen && (
          <MultiEditModal
            open={isModalOpen}
            handleClose={handleCloseModal}
            attribute={attribute}
            value={activeValue}
            handleSave={handleModalSave}
            groupList={groupList}
            entityData={entityData}
            entityDetailsFormikForm={entityDetailsFormikForm}
            handleAutoSave={handleAutoSave}
            isDerivedExtended={isDerivedExtended}
            usersList={finalUsersList}
            teamUsersList={teamUsersList}
            mode="edit"
            configColumn={configColumn}
            modelTemplateDetails={modelTemplateDetails}
            isUserAuthorizedToOverrideAttribute={
              isUserAuthorizedToOverrideAttribute
            }
          />
        )}
        {dataType === 'Document' && (
          <DocumentPreviewModal {...previewModalProps} />
        )}
      </>
    );
  }, [
    handleOpenModal,
    getModalButtonText,
    isModalOpen,
    handleCloseModal,
    attribute,
    activeValue,
    handleModalSave,
    groupList,
    entityData,
    handleClickAttribute,
    isDerived,
    inputType,
    isDerivedExtended,
    modelTemplateDetails,
    isNewVariant,
    dataType,
    docUploadStatus,
    hasError,
    showLabel,
    label,
    isRequired,
    entityDetailsFormikForm,
    finalUsersList,
    teamUsersList,
    configColumn,
    createRenderedLabel,
    showDraftChip,
    showHistoryIcon,
    draftAttributeValue,
    buildFieldLabel,
    handleModalTriggerBlur,
    handleResolvedAssociationChange,
    descriptiveDraftFooter,
    useDraftInputHighlight,
    theme,
    previewModalProps,
    existingDocument,
    handlePreviewExistingDocument,
  ]);

  // Normalizes a published group name to its numeric group ID in formik without
  // triggering draft auto-save. This is used by GroupSelectFieldComponent's
  // initialization effect so that the name→ID conversion doesn't create a phantom draft.
  const handleGroupNormalize = useCallback(
    (groupId) => {
      if (attribute?.id == null) return;
      entityDetailsFormikForm?.setFieldValue(attribute.id, groupId, false);
    },
    [attribute?.id, entityDetailsFormikForm]
  );

  // Render inline field for simple data types
  const renderInlineField = useCallback(() => {
    if (!attribute) return null;

    const isLongLabel = !isNewVariant && attribute?.label?.length > 90;
    const fieldLabel = isLongLabel
      ? `${attribute?.label?.slice(0, 90)}...`
      : attribute?.label;

    const useExternalLabel =
      isNewVariant ||
      showDraftChip ||
      dataType === 'Association' ||
      showHistoryIcon;
    const renderedLabel = useExternalLabel
      ? buildFieldLabel(fieldLabel)
      : createRenderedLabel();

    // For new-variant and draft fields the label row is rendered outside the input
    // so MUI InputLabel styles do not override the attribute label spec.
    const fieldProps = {
      label: useExternalLabel ? undefined : renderedLabel,
      titleLabel: attribute?.label,
      value: activeValue,
      onChange,
      attribute,
      showLabel: useExternalLabel ? false : showLabel,
      entityDetailsFormikForm,
      handleOpenDerivedModal,
      hideHeader,
      isLoadingConstraints: isLoadingFieldExceptSourceAttributes,
      isMultipleEditMode,
      configColumn,
      modelTemplateDetails,
      hasDraftChip: useDraftInputHighlight,
      onDraftBlur: persistDraftOnBlur,
      isNewVariant,
    };

    // Check for 'Segments' (case-insensitive)
    if (dataType?.toLowerCase() === 'segments') {
      return (
        <SegmentsAttribute
          {...fieldProps}
          entityData={entityData}
          allUserList={usersList}
          teamUsersList={teamUsersList}
          groupList={groupList}
          isUserAuthorizedForUpdateAttribute={
            isUserAuthorizedForUpdateAttribute
          }
          breadcrumbDetails={breadcrumbDetails}
          deleteNote={deleteNote}
          editNote={editNote}
          handleOpenNoteModal={handleOpenNoteModal}
          handleSetAddNoteModalHeading={handleSetAddNoteModalHeading}
          handleSetSelectedEntityToAddInEditor={
            handleSetSelectedEntityToAddInEditor
          }
          getAttComments={getAttComments}
          allAttributeChats={allAttributeChats}
          getOverrideAttributeValue={getOverrideAttributeValue}
          isMultipleEditMode={isMultipleEditMode}
          createNewSegmentRef={createNewSegmentRef}
          copySegmentRef={copySegmentRef}
          segmentButtonState={segmentButtonState}
          entitySelectedGroupId={entitySelectedGroupId}
          prevNodeGroupSelected={prevNodeGroupSelected}
        />
      );
    }
    if (dataType?.toLowerCase() === 'fieldchange') {
      return (
        <FieldChangeOtherDetails
          {...fieldProps}
          usersList={finalUsersList}
          teamUsersList={teamUsersList}
          groupList={groupList}
          mode={value?.disableEdit ? 'view' : 'edit'}
        />
      );
    }
    if (dataType === 'AttributeList') {
      return <AttributeListFieldComponent {...fieldProps} />;
    }
    if (dataType === 'ParameterTableList') {
      return <ParameterTableFieldComponent {...fieldProps} />;
    }
    if (dataType === 'EmailTemplateList') {
      return <EmailTemplateListFieldComponent {...fieldProps} />;
    }
    if (dataType === 'TemplateList') {
      return <TemplateListFieldComponent {...fieldProps} />;
    }
    if (dataType === 'FieldChangeActions') {
      return <FieldChangeActionsComponent {...fieldProps} />;
    }
    if (dataType === 'InventoryView') {
      return (
        <InventoryViewAttribute
          {...fieldProps}
          entityId={entityData?.entity_id}
        />
      );
    }

    if (dataType === 'Character') {
      switch (inputType) {
        case 'TEXTFIELD':
          return <TextFieldComponent {...fieldProps} />;
        case 'SINGLE_SELECT':
          return (
            <DropdownFieldComponent
              {...fieldProps}
              overrideDropdownOptions={attributeOverrideOptions}
            />
          );
        case 'MULTI_SELECT':
          return (
            <MultiSelectFieldComponent
              {...fieldProps}
              overrideDropdownOptions={attributeOverrideOptions}
            />
          );
        case 'Canvas':
          return <CanvasFieldRenderer {...fieldProps} />;
        default:
          return <TextFieldComponent {...fieldProps} />;
      }
    }

    if (dataType === 'User' && inputType === 'MULTI_SELECT') {
      return (
        <UserMultiSelectFieldComponent
          {...fieldProps}
          usersList={finalUsersList}
          formData={entityDetailsFormikForm?.values || {}}
          entityData={entityData}
        />
      );
    }

    if (dataType === 'Group' && inputType === 'MULTI_SELECT') {
      return (
        <GroupMultiSelectFieldComponent {...fieldProps} groupList={groupList} />
      );
    }

    // Handle other data types
    switch (dataType) {
      case 'Integer':
        return <IntegerFieldComponent {...fieldProps} />;
      case 'Decimal':
        return <DecimalFieldComponent {...fieldProps} />;
      case 'Date':
        return (
          <DateFieldComponent
            {...fieldProps}
            dateCalendarConstraints={dateCalendarConstraints}
          />
        );
      case 'User': {
        const userSelectField = (
          <UserSelectFieldComponent
            {...fieldProps}
            usersList={finalUsersList}
            groupDetails={groupDetails}
            formData={entityDetailsFormikForm?.values || {}}
            entityData={entityData}
            isMultipleEditMode={true}
            attributeValues={attributeValues}
          />
        );

        if (attribute?.name === 'assignedUser' && isHDFCUAMEnabled) {
          return (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
              }}
            >
              {showLabel && !useExternalLabel && renderedLabel}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  height: '36px',
                }}
              >
                {userSelectField}
              </Box>
            </Box>
          );
        }
        return userSelectField;
      }
      case 'Group':
        return (
          <GroupSelectFieldComponent
            {...fieldProps}
            groupList={groupList}
            onNormalize={handleGroupNormalize}
          />
        );
      case 'Association':
        if (attribute?.show_as_table) {
          return (
            <AssociationFieldTableViewComponent
              attribute={attribute}
              entityId={entityData?.entity_id}
              entityData={entityData}
            />
          );
        }
        return (
          <AssociationFieldComponent
            {...fieldProps}
            formData={entityDetailsFormikForm?.values || {}}
            entityData={entityData}
            onResolvedAssociationChange={handleResolvedAssociationChange}
          />
        );
      case 'Hyperlink':
        return <TextFieldComponent {...fieldProps} />;
      case 'HTML':
        return (
          <>
            {showLabel && !useExternalLabel && (
              <Box sx={{ position: 'relative', top: '4px' }}>
                {renderedLabel}
              </Box>
            )}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                height: '36px',
              }}
            >
              <LazyHtmlAttributePreviewLink
                attribute={attribute}
                entityId={entityData?.entity_id}
                showPdfDownload
              />
            </Box>
          </>
        );
      default:
        // Handle special cases based on input control type
        if (inputType === 'IFRAME') {
          // For IFRAME type, show modal trigger even if not in MODAL_DATA_TYPES
          return renderModalTrigger();
        }
        return;
    }
  }, [
    attribute,
    activeValue,
    onChange,
    finalUsersList,
    groupList,
    dataType,
    inputType,
    renderModalTrigger,
    handleOpenDerivedModal,
    createRenderedLabel,
    showDraftChip,
    buildFieldLabel,
    persistDraftOnBlur,
    handleAutoSave,
    isMultipleEditMode,
    entityDetailsFormikForm,
    isRequired,
    showLabel,
    isNewVariant,
    showAssociationRedirectIcon,
    dataType,
    isHDFCUAMEnabled,
    handleResolvedAssociationChange,
    descriptiveDraftFooter,
    useDraftInputHighlight,
    handleGroupNormalize,
  ]);

  // Decide whether to render modal trigger or inline field
  if (shouldOpenInModal) {
    return wrapWithDraftExtras(renderModalTrigger());
  }

  const fieldContent = wrapWithDraftExtras(renderInlineField());

  // Wrap with AI suggestion indicator if there is a suggestion
  if (aiSuggestion) {
    return (
      <AISuggestionIndicator
        suggestion={aiSuggestion}
        attributeId={attribute?.id}
        onAccept={onAcceptAISuggestion}
        onReject={onRejectAISuggestion}
        onUndo={onUndoAISuggestion}
      >
        {fieldContent}
      </AISuggestionIndicator>
    );
  }

  return fieldContent;
};

MultiEditAttributeField.propTypes = {
  attribute: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    label: PropTypes.string,
    data_type: PropTypes.string,
    input_control_type: PropTypes.string,
    is_derived: PropTypes.bool,
    permission: PropTypes.string,
    show_calculation_popUp: PropTypes.bool,
    is_editable: PropTypes.bool,
    drop_down_options: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.shape({}),
      ])
    ),
    show_as_table: PropTypes.bool,
    show_history: PropTypes.bool,
  }).isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.shape({}),
      ])
    ),
    PropTypes.shape({}),
  ]),
  onChange: PropTypes.func.isRequired,
  associationList: PropTypes.arrayOf(PropTypes.shape({})),
  entityData: PropTypes.shape({
    entity_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    entity_type: PropTypes.string,
  }),
  entityDetailsFormikForm: PropTypes.shape({
    getFieldProps: PropTypes.func,
    setFieldValue: PropTypes.func,
    errors: PropTypes.objectOf(PropTypes.string),
    touched: PropTypes.objectOf(PropTypes.bool),
    initialValues: PropTypes.objectOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.bool,
        PropTypes.arrayOf(
          PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
            PropTypes.shape({}),
          ])
        ),
        PropTypes.shape({}),
      ])
    ),
    values: PropTypes.objectOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.bool,
        PropTypes.arrayOf(
          PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
            PropTypes.shape({}),
          ])
        ),
        PropTypes.shape({}),
      ])
    ),
  }),
  handleAutoSave: PropTypes.func,
  groupDetails: PropTypes.shape({
    isGroupAdmin: PropTypes.bool,
    userName: PropTypes.string,
    userId: PropTypes.number,
    users: PropTypes.arrayOf(
      PropTypes.shape({
        userId: PropTypes.number,
        username: PropTypes.string,
      })
    ),
  }),
  isUserAuthorizedForUpdateAttribute: PropTypes.bool,
  breadcrumbDetails: PropTypes.shape({}),
  deleteNote: PropTypes.func,
  editNote: PropTypes.func,
  handleOpenNoteModal: PropTypes.func,
  handleSetAddNoteModalHeading: PropTypes.func,
  handleSetSelectedEntityToAddInEditor: PropTypes.func,
  getAttComments: PropTypes.func,
  allAttributeChats: PropTypes.arrayOf(PropTypes.shape({})),
  getOverrideAttributeValue: PropTypes.func,
  isMultipleEditMode: PropTypes.bool,
  createNewSegmentRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.shape({}) }),
  ]),
  copySegmentRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.shape({}) }),
  ]),
  segmentButtonState: PropTypes.string,
  entitySelectedGroupId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  prevNodeGroupSelected: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  handleOpenDerivedModal: PropTypes.func,
  hideHeader: PropTypes.bool,
  isUserAuthorizedToOverrideAttribute: PropTypes.bool,
  lookupSourceAttributes: PropTypes.arrayOf(PropTypes.number),
  autoCompleteSourceAttributes: PropTypes.arrayOf(PropTypes.number),
  overrideDropdownOptions: PropTypes.shape({}),
  isLoadingConstraints: PropTypes.bool,
  dateCalendarConstraints: PropTypes.shape({}),
  dateCalendarSourceAttributes: PropTypes.arrayOf(PropTypes.number),
  editableSourceAttributes: PropTypes.arrayOf(PropTypes.number),
  mandatorySourceAttributes: PropTypes.arrayOf(PropTypes.number),
  teamUsersList: PropTypes.arrayOf(PropTypes.shape({})),
  showLabel: PropTypes.bool,
  attributeValues: PropTypes.shape({}),
  savedAttributeValues: PropTypes.shape({}),
  // AI Autofill
  aiSuggestion: PropTypes.shape({
    oldValue: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.bool,
      PropTypes.arrayOf(PropTypes.string),
      PropTypes.shape({}),
    ]),
    newValue: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.bool,
      PropTypes.arrayOf(PropTypes.string),
      PropTypes.shape({}),
    ]),
    confidence: PropTypes.number,
    status: PropTypes.string,
  }),
  onAcceptAISuggestion: PropTypes.func,
  onRejectAISuggestion: PropTypes.func,
  onUndoAISuggestion: PropTypes.func,
  configColumn: PropTypes.bool,
  modelTemplateDetails: PropTypes.shape({}),
  isNewVariant: PropTypes.string,
  showDraftFooter: PropTypes.bool,
  descriptiveDraftFooter: PropTypes.bool,
  endAdornmentExtra: PropTypes.node,
  onHistoryClick: PropTypes.func,
};

export default MultiEditAttributeField;
